import React from "react";
import { ArrowRight, MapPin } from "lucide-react";
import Logo from "@/components/Logo";

// ============================================================
// MOCKUP C — Split Hero: Stylised Course + Live Scorecard
// Left: hand-drawn SVG of a golf hole (par-3 island green) on
// brand. Right: floating glass card with the eclectic.
// No stock photography. 100% on-brand.
// ============================================================

export default function HeroMockup3() {
  return (
    <div className="min-h-screen bg-app">
      <header className="border-b border-line bg-app/80 backdrop-blur sticky top-0 z-30">
        <div className="container mx-auto max-w-6xl px-5 sm:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo variant="icon" size={28} />
            <span className="text-[15px] font-semibold tracking-tight text-ink">Golf Eclectic</span>
          </div>
          <span className="text-[12px] text-ink-soft">Mockup C · Course + scorecard</span>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden">
          {/* Stylised course backdrop */}
          <div className="absolute inset-0 -z-0">
            <svg viewBox="0 0 1200 600" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
              <defs>
                <radialGradient id="sky" cx="30%" cy="30%" r="80%">
                  <stop offset="0%" stopColor="#EAF2EC" />
                  <stop offset="100%" stopColor="#F4F6F5" />
                </radialGradient>
                <linearGradient id="fairway" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#7BA88B" />
                  <stop offset="100%" stopColor="#3F6E55" />
                </linearGradient>
                <linearGradient id="green" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#A4C9A9" />
                  <stop offset="100%" stopColor="#6A9A7C" />
                </linearGradient>
                <linearGradient id="water" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#5A8AAE" />
                  <stop offset="100%" stopColor="#2F5673" />
                </linearGradient>
              </defs>
              <rect width="1200" height="600" fill="url(#sky)" />
              {/* distant hills */}
              <path d="M0,360 Q200,300 400,330 T800,310 T1200,340 L1200,600 L0,600 Z" fill="#D8E1DA" opacity="0.7" />
              <path d="M0,420 Q300,380 600,395 T1200,400 L1200,600 L0,600 Z" fill="#B9CDBE" opacity="0.85" />
              {/* fairway */}
              <path d="M-40,560 Q200,470 480,495 T960,460 T1240,510 L1240,640 L-40,640 Z" fill="url(#fairway)" />
              {/* water */}
              <ellipse cx="780" cy="420" rx="200" ry="50" fill="url(#water)" />
              {/* island green */}
              <ellipse cx="780" cy="412" rx="86" ry="22" fill="url(#green)" />
              {/* flagstick */}
              <line x1="800" y1="412" x2="800" y2="350" stroke="#0E1717" strokeWidth="2.5" />
              <path d="M800,350 L838,360 L800,372 Z" fill="#14855B" />
              {/* hole cup */}
              <ellipse cx="800" cy="412" rx="3.5" ry="1.5" fill="#0E1717" />
              {/* bunker dots */}
              <ellipse cx="350" cy="540" rx="58" ry="14" fill="#E9DCB8" />
              <ellipse cx="1050" cy="500" rx="42" ry="12" fill="#E9DCB8" />
            </svg>
            {/* subtle overlay to keep text readable */}
            <div className="absolute inset-0 bg-app/55" />
          </div>

          <div className="container mx-auto max-w-6xl px-5 sm:px-8 relative z-10">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 pt-12 sm:pt-24 pb-20 items-center min-h-[560px]">
              <div className="lg:col-span-7">
                <p className="ge-eyebrow mb-4 inline-flex items-center gap-1.5">
                  <MapPin className="w-3 h-3" /> Built for UK clubs & societies
                </p>
                <h1 className="text-4xl sm:text-5xl lg:text-[64px] font-semibold tracking-[-0.03em] text-ink leading-[1.04]">
                  The perfect round, <span className="text-accent">built hole by hole.</span>
                </h1>
                <p className="text-[17px] sm:text-lg text-ink-muted mt-6 max-w-xl leading-relaxed">
                  Play your rounds. Enter each scorecard. Watch as your best score on
                  every hole comes together into one running eclectic — and see exactly
                  where you stand in the field.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-2.5">
                  <button className="ge-btn-primary !px-5 !py-3 !text-[14.5px] justify-center">Sign in <ArrowRight className="w-4 h-4" /></button>
                  <button className="ge-btn-outline !px-5 !py-3 !text-[14.5px] justify-center">Continue with Google</button>
                </div>
              </div>

              <div className="lg:col-span-5">
                {/* Glassmorphism floating card */}
                <div
                  className="rounded-2xl p-4 shadow-2xl border border-white/60"
                  style={{ background: "rgba(255,255,255,0.78)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)" }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10.5px] uppercase tracking-[0.14em] text-accent font-semibold">Your eclectic</p>
                      <p className="text-[14.5px] font-semibold text-ink">After 4 rounds</p>
                    </div>
                    <span className="text-[10.5px] px-2 py-0.5 rounded-full bg-accent-tint-2 text-accent-deep font-medium">4th / 18</span>
                  </div>

                  <div className="mt-3 grid grid-cols-9 gap-1">
                    {[3,4,4,3,4,5,3,3,4].map((s, i) => {
                      const par = [4,4,5,3,4,5,4,3,4][i];
                      const d = s - par;
                      const cls = d < 0 ? "bg-accent text-white" : d === 0 ? "bg-white text-ink" : "bg-surface-2 text-ink-muted";
                      return (
                        <div key={i} className={`rounded py-1 text-center border border-line/40 ${cls}`}>
                          <div className="text-[7.5px] opacity-70 font-mono">{i + 1}</div>
                          <div className="text-[12px] font-semibold tabular-nums">{s}</div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-3 flex items-center justify-between rounded-lg bg-ink text-white px-3 py-2.5">
                    <span className="text-[11.5px] uppercase tracking-[0.1em] text-white/60 font-semibold">Net eclectic</span>
                    <span className="text-2xl font-semibold tabular-nums">33</span>
                  </div>

                  <p className="text-[10.5px] text-ink-soft text-center pt-2">Countback applied · live across all rounds</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
